import { useEffect, useState } from "react";
import { LOGO_MARK } from "../lib/api";

/**
 * Bouton "Installer l'app" — capture l'événement beforeinstallprompt sur Android/Chrome/Edge
 * Affiche des instructions manuelles sur iOS Safari (qui ne supporte pas la prompt native)
 */
export default function InstallAppButton() {
  const [deferred, setDeferred] = useState(null);
  const [showIosHelp, setShowIosHelp] = useState(false);
  const [installed, setInstalled] = useState(false);
  const isIos = typeof navigator !== "undefined" && /iphone|ipad|ipod/i.test(navigator.userAgent);
  const isStandalone = typeof window !== "undefined" && (window.matchMedia?.("(display-mode: standalone)").matches || navigator.standalone);

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => setInstalled(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (isStandalone || installed) return null;

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") setInstalled(true);
      setDeferred(null);
    } else if (isIos) {
      setShowIosHelp(true);
    } else {
      setShowIosHelp(true); // fallback with instructions
    }
  };

  return (
    <>
      <button onClick={install} className="btn-gold" data-testid="btn-install-app" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
        <i className="fa-solid fa-mobile-screen-button" /> Installer l'app
      </button>

      {showIosHelp && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }} onClick={() => setShowIosHelp(false)} data-testid="install-help-modal">
          <div style={{ background: "#fff", borderRadius: 8, maxWidth: 480, padding: 32, boxShadow: "0 30px 80px rgba(0,0,0,0.4)" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ textAlign: "center", marginBottom: 24 }}>
              <img src={LOGO_MARK} alt="Amatour" style={{ height: 64, margin: "0 auto 12px" }} />
              <h3 className="font-display" style={{ fontSize: 24, color: "var(--amatour-ink)" }}>Installer Amatour Festival</h3>
              <p className="italic-accent" style={{ marginTop: 8 }}>Ajoutez le site à votre écran d'accueil pour l'utiliser comme une vraie app</p>
            </div>

            {isIos ? (
              <div style={{ color: "var(--amatour-ink-2)" }}>
                <div className="font-editorial" style={{ color: "var(--amatour-gold-deep)", fontSize: 12, marginBottom: 8 }}>iPhone / iPad · Safari</div>
                <ol style={{ paddingLeft: 20, lineHeight: 1.7 }}>
                  <li>Ouvrez ce site dans <strong>Safari</strong></li>
                  <li>Touchez l'icône <i className="fa-solid fa-arrow-up-from-bracket" /> <strong>Partager</strong> en bas de l'écran</li>
                  <li>Faites défiler et touchez <strong>"Sur l'écran d'accueil"</strong></li>
                  <li>Touchez <strong>Ajouter</strong> en haut à droite</li>
                </ol>
              </div>
            ) : (
              <div style={{ color: "var(--amatour-ink-2)" }}>
                <div className="font-editorial" style={{ color: "var(--amatour-gold-deep)", fontSize: 12, marginBottom: 8 }}>Android · Chrome / Edge</div>
                <ol style={{ paddingLeft: 20, lineHeight: 1.7 }}>
                  <li>Touchez le menu <strong>⋮</strong> en haut à droite</li>
                  <li>Choisissez <strong>"Installer l'application"</strong> ou <strong>"Ajouter à l'écran d'accueil"</strong></li>
                  <li>Confirmez avec <strong>Installer</strong></li>
                </ol>
                <div style={{ marginTop: 16, padding: 12, background: "rgba(212,175,55,0.1)", borderLeft: "3px solid var(--amatour-gold)", borderRadius: 4, fontSize: 13 }}>
                  <i className="fa-solid fa-lightbulb" style={{ color: "var(--amatour-warm-deep)" }} /> Sur ordinateur : cliquez sur l'icône <i className="fa-solid fa-desktop" /> qui apparaît dans la barre d'adresse
                </div>
              </div>
            )}

            <button onClick={() => setShowIosHelp(false)} className="btn-outline" style={{ width: "100%", marginTop: 24 }} data-testid="install-help-close">
              Fermer
            </button>
          </div>
        </div>
      )}
    </>
  );
}
